import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import { FaSearch } from 'react-icons/fa';
import { API_BASE, parseJsonResponse, getUserErrorMessage } from '../../utils/apiBase';
import { authFetch } from '../../utils/apiAuth';
import { getCurrentUser } from '../../utils/authSession';
import SuperPanelShell from '../../components/super/SuperPanelShell';
import './superPanel.css';

const roleLabel = {
  superadmin: 'Super Admin',
  admin: 'Admin',
};

const HIDDEN_PANEL_EMAIL = 'hammadsaeed083@gmail.com';

function isHiddenPanelUser(user) {
  return user?.email?.trim().toLowerCase() === HIDDEN_PANEL_EMAIL;
}

function getInitials(name = '') {
  return name
    .split(' ')
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase() || '?';
}

function userId(user) {
  return String(user?.id || user?._id || '');
}

export default function AdminUsers() {
  const navigate = useNavigate();
  const currentUser = getCurrentUser();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [deletingId, setDeletingId] = useState('');

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await authFetch(`${API_BASE}/v1/user/panel`);
      const result = await parseJsonResponse(response);
      setUsers((result.data || []).filter((user) => !isHiddenPanelUser(user)));
    } catch (error) {
      setUsers([]);
      toast.error(getUserErrorMessage(error, 'Unable to load admin users.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const filtered = users.filter((user) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      user.name?.toLowerCase().includes(q) ||
      user.email?.toLowerCase().includes(q) ||
      user.phone?.toLowerCase().includes(q)
    );
  });

  const superCount = users.filter((u) => u.role === 'superadmin').length;
  const adminCount = users.filter((u) => u.role === 'admin').length;

  const handleDelete = async (user) => {
    if (!window.confirm(`Delete admin account for ${user.name}?`)) return;

    setDeletingId(userId(user));
    try {
      const response = await authFetch(`${API_BASE}/v1/user/panel/${userId(user)}`, {
        method: 'DELETE',
      });
      await parseJsonResponse(response);
      toast.success('Admin user deleted');
      fetchUsers();
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'Unable to delete admin user.'));
    } finally {
      setDeletingId('');
    }
  };

  return (
    <SuperPanelShell
      title="Admin Users"
      titleExtra={<span className="sp-count-pill">{users.length} total</span>}
      breadcrumb="Super Panel • User Management"
      action={
        <button type="button" className="sp-btn sp-btn--gold" onClick={() => navigate('/super/admins/add')}>
          + Create Admin
        </button>
      }
    >
      <Toaster
        position="top-center"
        toastOptions={{
          style: { background: '#12121f', color: '#f5d76e', border: '1px solid rgba(212,175,55,0.3)' },
        }}
      />

      <div className="sp-stats-row">
        <div className="sp-stat-card">
          <span className="sp-stat-label">Total Admins</span>
          <strong className="sp-stat-value">{users.length}</strong>
          <span className="sp-stat-hint">All panel accounts</span>
        </div>
        <div className="sp-stat-card">
          <span className="sp-stat-label">Super Admins</span>
          <strong className="sp-stat-value sp-stat-gold">{superCount}</strong>
          <span className="sp-stat-hint">Root access holders</span>
        </div>
        <div className="sp-stat-card">
          <span className="sp-stat-label">Regular Admins</span>
          <strong className="sp-stat-value">{adminCount}</strong>
          <span className="sp-stat-hint">Standard panel access</span>
        </div>
      </div>

      <div className="sp-card">
        <div className="sp-card-toolbar">
          <div className="sp-search">
            <FaSearch />
            <input
              type="search"
              placeholder="Search by name, email, or phone..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoComplete="off"
            />
          </div>
        </div>

        <div className="sp-card-body">
          {loading ? (
            <p className="sp-muted">Loading admin users...</p>
          ) : filtered.length === 0 ? (
            <p className="sp-muted">No admin users found.</p>
          ) : (
            <div className="sp-table-wrap">
              <table className="sp-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Role</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((user) => {
                    const id = userId(user);
                    const isSelf = id && id === userId(currentUser);
                    return (
                      <tr key={id || user.email}>
                        <td>
                          <div className="sp-user-cell">
                            <div className="sp-user-avatar">{getInitials(user.name)}</div>
                            <span className="sp-user-name">
                              {user.name}
                              {isSelf && <span className="sp-you-badge">You</span>}
                            </span>
                          </div>
                        </td>
                        <td>{user.email}</td>
                        <td>{user.phone}</td>
                        <td>
                          <span className={`sp-role-badge sp-role-${user.role}`}>
                            {roleLabel[user.role] || user.role}
                          </span>
                        </td>
                        <td>
                          <div className="sp-actions">
                            <button
                              type="button"
                              className="sp-btn sp-btn--ghost sp-btn--sm"
                              onClick={() => navigate(`/super/admins/edit/${id}`)}
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              className="sp-btn sp-btn--danger sp-btn--sm"
                              disabled={isSelf || deletingId === id}
                              onClick={() => handleDelete({ ...user, id })}
                            >
                              {deletingId === id ? 'Deleting...' : 'Delete'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </SuperPanelShell>
  );
}
